import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { motion } from 'framer-motion';
import trackingManager from '../../../lib/tracking';
import { EventType } from '../../../lib/tracking/eventTracker';
import { T } from '@tolgee/react';
import { getOperatorBasePath, getOperatorPanelLabel, isDistributorRole } from '../../commercial/utils/operator';

// Componente para cada casilla de funcionalidad
const AccountCard = ({
  title,
  description,
  icon,
  linkTo,
  color = '#f6cf02'
}: {
  title: string,
  description: string,
  icon: React.ReactNode,
  linkTo: string,
  color?: string
}) => (
  <Link
    to={linkTo}
    className="block bg-white rounded-2xl shadow-md border-2 border-gray-100 hoverable-card transition-all duration-300 group"
  >
    <div className="p-6">
      <div
        className="w-16 h-16 mb-4 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
        style={{backgroundColor: color}}
      >
        <div style={{color: '#0d0d0d'}}>
          {icon}
        </div>
      </div>
      <h3 className="text-xl font-bold mb-2 group-hover:text-gray-700 transition-colors" style={{color: '#0d0d0d'}}>
        <T keyName={title}>{title}</T>
      </h3>
      <p className="text-gray-600 leading-relaxed">
        <T keyName={description}>{description}</T>
      </p>
    </div>
  </Link>
);

const AccountPage = () => {
  const { auth } = useAuth();
  const [greeting, setGreeting] = useState('auth.account.good_morning');

  // Comprobar si el usuario es administrador
  const isAdmin = auth.user?.role === 'admin' || auth.user?.role === 'beeadmin';
  const isDistributor = isDistributorRole(auth.user?.role);
  const canAccessOperatorPanel =
    auth.user?.role === 'commercial' ||
    auth.user?.role === 'distributor' ||
    auth.user?.role === 'admin' ||
    auth.user?.role === 'beeadmin';

  // Registrar vista de página
  useEffect(() => {
    // Registrar vista de página usando EventTracker
    trackingManager.events.track(
      EventType.PAGE_VIEW,
      {
        path: window.location.pathname,
        pageTitle: document.title,
        customData: { section: 'account_dashboard' }
      }
    );

    // Rastrear evento personalizado usando EventTracker
    if (auth.user) {
      trackingManager.events.track(
        EventType.CUSTOM,
        {
          eventName: 'view_account_dashboard',
          userId: auth.user?.id,
          authenticated: auth.isAuthenticated
        }
      );
    }
  }, [auth.isAuthenticated, auth.user]);

  // Actualizar el saludo según la hora del día
  useEffect(() => {
    const hour = new Date().getHours();

    if (hour >= 6 && hour < 12) {
      setGreeting('auth.account.good_morning');
    } else if (hour >= 12 && hour < 19) {
      setGreeting('auth.account.good_afternoon');
    } else {
      setGreeting('auth.account.good_evening');
    }
  }, []);

  // Estados para animación de notificaciones
  const notificationsVariants = {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: 0.5
      }
    }
  };

  // Estados para animación de tarjetas
  const cardVariants = {
    initial: { opacity: 0, y: 20 },
    animate: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: 0.1 * i
      }
    })
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#f2ede4'}}>
      {/* Hero Section */}
      <div className="py-24 bg-white">
        <div className="content-container">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center shadow-xl" style={{backgroundColor: '#f6cf02'}}>
              <span className="text-3xl font-bold" style={{color: '#0d0d0d'}}>
                {auth.user?.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold mb-4 tracking-tight" style={{color: '#0d0d0d'}}>
              <T keyName={greeting}>{greeting}</T>, {auth.user?.name.split(' ')[0]}
            </h1>
            <p className="text-xl md:text-2xl font-light text-gray-600 mb-8 leading-relaxed">
              <T keyName="auth.account.welcome">Bienvenido a tu cuenta Jarad. Gestiona tus pedidos, direcciones y preferencias desde aquí.</T>
            </p>
          </motion.div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="py-16">
        <div className="content-container">
          {/* Notificaciones del usuario */}
          <motion.div
            className="mb-12 bg-white border-2 rounded-2xl p-6 flex items-start shadow-md"
            style={{borderColor: '#f6cf02'}}
            variants={notificationsVariants}
            initial="initial"
            animate="animate"
          >
            <div className="flex-shrink-0 p-2 rounded-full" style={{backgroundColor: '#f6cf02'}}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{color: '#0d0d0d'}} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-bold mb-2" style={{color: '#0d0d0d'}}>
                <T keyName="auth.account.welcome_title">Estamos encantados de tenerte en Jarad</T>
              </h3>
              <p className="text-gray-600 leading-relaxed">
                <T keyName="auth.account.welcome_message">Si necesitas ayuda con cualquier cosa, no dudes en contactarnos. Nuestro equipo está aquí para ayudarte.</T>
              </p>
            </div>
          </motion.div>

          {/* Tarjetas de acceso */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Tarjeta del panel de administración (solo para admin y beeadmin) */}
            {isAdmin && (
              <motion.div
                custom={0}
                variants={cardVariants}
                initial="initial"
                animate="animate"
              >
                <AccountCard
                  title="auth.account.admin_panel"
                  description="auth.account.admin_panel_desc"
                  linkTo="/admin/dashboard"
                  color="#0d0d0d"
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  }
                />
              </motion.div>
            )}

            {canAccessOperatorPanel && (
              <motion.div
                custom={isAdmin ? 1 : 0}
                variants={cardVariants}
                initial="initial"
                animate="animate"
              >
                <AccountCard
                  title={getOperatorPanelLabel(auth.user?.role)}
                  description={isDistributor ? 'Gestiona pedidos con envío directo y destinatarios finales' : 'Accede a tu panel de gestión B2B'}
                  linkTo={getOperatorBasePath(auth.user?.role)}
                  color="#d4f4dd"
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M6 11h12M8 15h8M5 19h14" />
                    </svg>
                  }
                />
              </motion.div>
            )}

            <motion.div
            custom={isAdmin ? (canAccessOperatorPanel ? 2 : 1) : (canAccessOperatorPanel ? 1 : 0)}
            variants={cardVariants}
            initial="initial"
            animate="animate"
          >
            <AccountCard
              title="auth.account.my_orders"
              description="auth.account.my_orders_desc"
              linkTo="/orders"
              color="#8ed1fc"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
              }
            />
          </motion.div>

          <motion.div
            custom={isAdmin ? (canAccessOperatorPanel ? 3 : 2) : (canAccessOperatorPanel ? 2 : 1)}
            variants={cardVariants}
            initial="initial"
            animate="animate"
          >
            <AccountCard
              title="auth.account.favorites"
              description="auth.account.favorites_desc"
              linkTo="/account/favorites"
              color="#e8b4a0"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
              }
            />
          </motion.div>

          <motion.div
            custom={isAdmin ? (canAccessOperatorPanel ? 4 : 3) : (canAccessOperatorPanel ? 3 : 2)}
            variants={cardVariants}
            initial="initial"
            animate="animate"
          >
            <AccountCard
              title="auth.account.addresses"
              description="auth.account.addresses_desc"
              linkTo="/account/addresses"
              color="#c7c7c7"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
              }
            />
            </motion.div>

            <motion.div
            custom={isAdmin ? (canAccessOperatorPanel ? 5 : 4) : (canAccessOperatorPanel ? 4 : 3)}
            variants={cardVariants}
            initial="initial"
            animate="animate"
          >
            <AccountCard
              title="auth.account.payment_methods"
              description="auth.account.payment_methods_desc"
              linkTo="/account/payment-methods"
              color="#f6cf02"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
              }
            />
            </motion.div>

            {isDistributor && (
              <motion.div
                custom={isAdmin ? (canAccessOperatorPanel ? 6 : 5) : (canAccessOperatorPanel ? 5 : 4)}
                variants={cardVariants}
                initial="initial"
                animate="animate"
              >
                <AccountCard
                  title="Emails para cliente final"
                  description="Configura logo, cabecera, colores y reply-to para emails logísticos"
                  linkTo="/account/drop-shipping-emails"
                  color="#fde68a"
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8m-2 10H5a2 2 0 01-2-2V8a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2z" />
                    </svg>
                  }
                />
              </motion.div>
            )}

            <motion.div
            custom={isAdmin ? (isDistributor ? 7 : canAccessOperatorPanel ? 6 : 5) : (isDistributor ? 6 : canAccessOperatorPanel ? 5 : 4)}
            variants={cardVariants}
            initial="initial"
            animate="animate"
          >
            <AccountCard
              title="auth.account.security"
              description="auth.account.security_desc"
              linkTo="/account/password"
              color="#8ed1fc"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              }
            />
            </motion.div>

            <motion.div
            custom={isAdmin ? (isDistributor ? 8 : canAccessOperatorPanel ? 7 : 6) : (isDistributor ? 7 : canAccessOperatorPanel ? 6 : 5)}
            variants={cardVariants}
            initial="initial"
            animate="animate"
          >
            <AccountCard
              title="auth.account.preferences"
              description="auth.account.preferences_desc"
              linkTo="/account/preferences"
              color="#e8b4a0"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              }
            />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
